import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(username, password);
      navigate("/");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="grid min-h-screen grid-cols-1 bg-white lg:grid-cols-[0.95fr_1.05fr]" data-testid="login-page">
      <section className="bdx-grid-pattern relative flex min-h-[42vh] flex-col overflow-hidden bg-[#202225] px-7 py-9 text-white sm:px-12 lg:min-h-screen lg:px-16 lg:py-14 xl:px-20" aria-label="BDX module housing application">
        <div className="flex items-center gap-3 text-[10px] font-semibold uppercase text-gray-400" style={{ letterSpacing: "0.15em" }}>
          <span className="h-2 w-2 bg-[#8b68ad]" /> BDX Operational Application
        </div>

        <div className="my-auto max-w-2xl py-14 lg:py-20">
          <div className="mb-6 flex items-center gap-3 text-xs font-semibold uppercase text-gray-400" style={{ letterSpacing: "0.14em" }}>
            <span className="h-px w-10 bg-[#8b68ad]" /> Modular Housing Delivery
          </div>
          <h1 className="font-heading text-4xl font-bold leading-[1.08] text-white sm:text-5xl lg:text-6xl">
            Module Housing<br />Projects
          </h1>
          <p className="mt-6 max-w-lg text-base leading-relaxed text-gray-300 sm:text-lg">
            SSH &amp; MMC electrical project control for structured programme delivery.
          </p>
        </div>

        <div className="border-t border-white/12 pt-6">
          <div className="text-[10px] font-semibold uppercase text-gray-500" style={{ letterSpacing: "0.14em" }}>Project controls · Electrical lifecycle · Commercial tracking</div>
        </div>
      </section>

      <section className="flex min-h-[58vh] flex-col bg-[#fbfaf9] px-7 py-9 sm:px-12 lg:min-h-screen lg:px-16 lg:py-12 xl:px-24" aria-label="Sign in">
        <div className="border-b border-gray-200 pb-7" data-testid="login-partnership-brands">
          <div className="mb-4 text-[9px] font-semibold uppercase text-gray-400" style={{ letterSpacing: "0.14em" }}>Programme Partnership</div>
          <div className="flex flex-wrap items-end gap-x-10 gap-y-5">
            <div>
              <div className="mb-2 text-[9px] font-semibold uppercase text-gray-400" style={{ letterSpacing: "0.13em" }}>Working with</div>
              <img src="/logos/blg.png" alt="Better Living Group" className="h-12 w-auto object-contain" data-testid="login-blg-logo" />
            </div>
            <div className="hidden h-12 w-px bg-gray-300 sm:block" />
            <div>
              <div className="mb-2 text-[9px] font-semibold uppercase text-gray-400" style={{ letterSpacing: "0.13em" }}>For</div>
              <img src="/logos/hv.png" alt="Homes Victoria" className="h-9 max-w-[210px] object-contain" data-testid="login-hv-logo" />
            </div>
          </div>
        </div>

        <div className="my-auto w-full max-w-md py-12">
          <img src="/logos/bdx.png" alt="BDX Group" className="h-[72px] w-auto object-contain sm:h-[82px]" data-testid="login-bdx-logo" />
          <div className="mt-7 border-l-2 border-[#765297] pl-3">
            <div className="text-[10px] font-semibold uppercase text-[#604184]" style={{ letterSpacing: "0.15em" }}>Authorised Access</div>
            <p className="mt-1.5 text-sm leading-relaxed text-gray-500">Enter your assigned Module Housing Projects credentials.</p>
          </div>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
            <div>
              <label htmlFor="login-username" className="bdx-label mb-2 block">Username</label>
              <input id="login-username" data-testid="login-username-input" className="bdx-input h-12 bg-white" value={username} onChange={(e) => setUsername(e.target.value)} autoFocus autoComplete="username" />
            </div>
            <div>
              <label htmlFor="login-password" className="bdx-label mb-2 block">Password</label>
              <input id="login-password" data-testid="login-password-input" type="password" className="bdx-input h-12 bg-white" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />
            </div>
            {error && <div role="alert" data-testid="login-error" className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
            <button data-testid="login-submit-button" disabled={busy} className="bdx-btn-primary h-12 w-full justify-between px-5">
              <span>{busy ? "Signing in…" : "Sign In"}</span>
              <ArrowRight size={17} strokeWidth={1.8} />
            </button>
          </form>

          <div className="mt-7 flex items-center gap-2 border-t border-gray-200 pt-5 text-xs text-gray-500" data-testid="login-security-note">
            <ShieldCheck size={15} strokeWidth={1.7} className="text-[#604184]" /> Secured access to the BDX cloud project dataset
          </div>
        </div>

        <div className="text-[10px] font-medium uppercase text-gray-400" style={{ letterSpacing: "0.1em" }}>BDX Group · Module Housing Projects</div>
      </section>
    </main>
  );
}